import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Swap a cart line's variant (preserving quantity). Shopify cart lines can't
 * have their variant changed in place — this adds the new variant then
 * removes the old line, the same add-then-remove technique used for the
 * free-gift variant swap in cart-rewards.js.
 * @param {HTMLElement} host — dispatches the CartLinesUpdateEvent
 * @param {string} lineKey
 * @param {number} quantity
 * @param {string | number} newVariantId
 */
async function swapCartLineVariant(host, lineKey, quantity, newVariantId) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
    .filter(Boolean);

  const addBody = new FormData();
  addBody.append('id', String(newVariantId));
  addBody.append('quantity', String(quantity));

  const addResponse = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: addBody,
  }).then((r) => r.json());

  if (addResponse.status) {
    console.warn('[cart-item-variant-swap] could not add new variant:', addResponse.description);
    return false;
  }

  const removeResponse = await fetch(
    Theme.routes.cart_change_url,
    fetchConfig('json', {
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    })
  ).then((r) => r.json());

  const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((r) => r.json());

  host.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart-item-variant-swap',
      lines: [{ id: lineKey, quantity: 0 }],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-item-variant-swap-component',
          didError: false,
          sections: removeResponse.sections,
        },
      }),
    })
  );
  return true;
}

/**
 * Single-select variant swap — used when a product has only one option (or
 * an option combination other than exactly Color + Size), via
 * `<select data-swap-variant-select data-line-key="..." data-quantity="...">`.
 *
 * @extends {HTMLElement}
 */
class CartItemVariantSwapComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = async (event) => {
    const select = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-swap-variant-select]');
    if (!(select instanceof HTMLSelectElement) || select.disabled) return;

    const lineKey = select.dataset.lineKey;
    const quantity = Number(select.dataset.quantity) || 1;
    const newVariantId = select.value;
    const previousVariantId = select.dataset.currentVariantId;
    if (!lineKey || !newVariantId || newVariantId === previousVariantId) return;

    select.disabled = true;
    const ok = await swapCartLineVariant(this, lineKey, quantity, newVariantId);
    if (!ok) select.disabled = false;
  };
}

/**
 * Two-part picker for products with exactly a Color + Size option pair:
 * color swatch dots (matching the product page's swatch style) plus a size
 * `<select>`. Resolves the matching variant from the embedded variant list
 * (`data-variants`) whenever either control changes, then swaps the cart
 * line to it.
 *
 * @extends {HTMLElement}
 */
class CartItemVariantPickerComponent extends HTMLElement {
  /** @type {boolean} */
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onSwatchClick);
    this.addEventListener('change', this.#onSizeChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onSwatchClick);
    this.removeEventListener('change', this.#onSizeChange);
  }

  /** @param {MouseEvent} event */
  #onSwatchClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[data-swap-color-value]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    this.#resolveAndSwap(button.dataset.swapColorValue, undefined);
  };

  /** @param {Event} event */
  #onSizeChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const select = target.closest('[data-swap-size-select]');
    if (!(select instanceof HTMLSelectElement)) return;
    this.#resolveAndSwap(undefined, select.value);
  };

  /**
   * @param {string | undefined} color
   * @param {string | undefined} size
   */
  async #resolveAndSwap(color, size) {
    if (this.#busy) return;

    /** @type {{ id: number, option1: string | null, option2: string | null, option3: string | null, available: boolean }[]} */
    let variants;
    try {
      variants = JSON.parse(this.dataset.variants ?? '[]');
    } catch {
      return;
    }

    const colorPos = Number(this.dataset.colorPosition);
    const sizePos = Number(this.dataset.sizePosition);
    const colorKey = `option${colorPos}`;
    const sizeKey = `option${sizePos}`;

    const currentColor =
      color ??
      this.querySelector('.cart-items__color-swatch--selected')?.dataset.swapColorValue ??
      this.dataset.fixedColor;
    const sizeSelect = this.querySelector('[data-swap-size-select]');
    const currentSize = size ?? (sizeSelect instanceof HTMLSelectElement ? sizeSelect.value : undefined);

    const match = variants.find((v) => v[colorKey] === currentColor && v[sizeKey] === currentSize);
    if (!match) {
      console.warn('[cart-item-variant-swap] no variant matches', { currentColor, currentSize, colorKey, sizeKey });
      return;
    }
    if (!match.available) {
      console.warn('[cart-item-variant-swap] matched variant is unavailable', match);
      return;
    }

    const lineKey = this.dataset.lineKey;
    const quantity = Number(this.dataset.quantity) || 1;
    const previousVariantId = this.dataset.currentVariantId;
    if (!lineKey || String(match.id) === previousVariantId) return;

    this.#busy = true;
    this.classList.add('cart-items__variant-picker--busy');
    try {
      await swapCartLineVariant(this, lineKey, quantity, match.id);
    } finally {
      this.#busy = false;
      this.classList.remove('cart-items__variant-picker--busy');
    }
  }
}

if (!customElements.get('cart-item-variant-swap')) {
  customElements.define('cart-item-variant-swap', CartItemVariantSwapComponent);
}
if (!customElements.get('cart-item-variant-picker')) {
  customElements.define('cart-item-variant-picker', CartItemVariantPickerComponent);
}
